import React, { useMemo, useState } from 'react';
import { Lead } from '../../services/leadService';
import {
    ActiveProjectPoint,
    LEAD_STATUSES,
    LEAD_STATUS_LABELS,
    LeadStatus,
    computeLeadPriority,
} from '../../services/leadLogic';
import { BRANCHE_OPTIONS, brancheLabel } from '../../services/branchen';
import { ChevronRight, Search } from 'lucide-react';

/**
 * Listenansicht der Pipeline mit den Filtern aus dem Plan (Branche,
 * Bundesland, Status, Quelle, Owner).
 *
 * Sie ist zugleich die Tastatur-/Screenreader-Alternative zum Kanban
 * (ERP-Plan 11, „Accessibility"). Die Filterwerte werden aus dem tatsaechlich
 * vorhandenen Bestand abgeleitet statt fest verdrahtet — ein Auswahlfeld mit
 * Bundeslaendern, in denen es keinen einzigen Lead gibt, hilft niemandem.
 */
interface Props {
    leads: Lead[];
    activeProjects: ActiveProjectPoint[];
    /** Anzeigenamen je Profil-Id fuer den Owner-Filter. */
    ownerNames?: Map<string, string>;
    onSelect: (lead: Lead) => void;
}

const STATUS_BADGE: Record<LeadStatus, string> = {
    neu: 'bg-blue-50 text-blue-600',
    qualifiziert: 'bg-sky-50 text-sky-600',
    kontaktiert: 'bg-teal-50 text-teal-600',
    angebot: 'bg-amber-50 text-amber-700',
    gewonnen: 'bg-emerald-50 text-emerald-700',
    verloren: 'bg-slate-100 text-slate-500',
    gesperrt: 'bg-red-50 text-red-700',
};

const ALLE = '';

/** Sortiert und ohne Leerwerte — Grundlage jedes abgeleiteten Filters. */
const werte = (leads: Lead[], feld: (l: Lead) => string | undefined): string[] =>
    Array.from(new Set(leads.map(feld).filter((v): v is string => !!v))).sort((a, b) =>
        a.localeCompare(b, 'de'),
    );

export const LeadsListView: React.FC<Props> = ({ leads, activeProjects, ownerNames, onSelect }) => {
    const [suche, setSuche] = useState('');
    const [status, setStatus] = useState<string>(ALLE);
    const [branche, setBranche] = useState<string>(ALLE);
    const [bundesland, setBundesland] = useState<string>(ALLE);
    const [quelle, setQuelle] = useState<string>(ALLE);
    const [owner, setOwner] = useState<string>(ALLE);

    const bundeslaender = useMemo(() => werte(leads, (l) => l.bundesland), [leads]);
    const quellen = useMemo(() => werte(leads, (l) => l.quelle), [leads]);
    const owners = useMemo(() => werte(leads, (l) => l.ownerUserId), [leads]);
    // Nur Branchen anbieten, die im Bestand vorkommen — mit Label aus dem
    // kontrollierten Vokabular.
    const branchen = useMemo(() => {
        const vorhanden = new Set(leads.map((l) => l.branche).filter(Boolean));
        return BRANCHE_OPTIONS.filter((o) => vorhanden.has(o.value));
    }, [leads]);

    const gefiltert = useMemo(() => {
        const q = suche.trim().toLowerCase();
        return leads
            .filter((l) => {
                if (status && l.status !== status) return false;
                if (branche && l.branche !== branche) return false;
                if (bundesland && l.bundesland !== bundesland) return false;
                if (quelle && l.quelle !== quelle) return false;
                if (owner && l.ownerUserId !== owner) return false;
                if (!q) return true;
                return [l.name, l.city, l.zip, l.website, l.ansprechpartner]
                    .some((v) => (v || '').toLowerCase().includes(q));
            })
            .map((lead) => ({ lead, prio: computeLeadPriority(lead, activeProjects) }))
            .sort((a, b) => b.prio.priority - a.prio.priority);
    }, [leads, suche, status, branche, bundesland, quelle, owner, activeProjects]);

    const zurueckgesetzt = !status && !branche && !bundesland && !quelle && !owner && !suche;

    const select = (
        id: string, label: string, value: string,
        onChange: (v: string) => void,
        options: { value: string; label: string }[],
    ) => (
        <div>
            <label htmlFor={id} className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                {label}
            </label>
            <select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500/20"
            >
                <option value={ALLE}>Alle</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );

    return (
        <div className="space-y-4">
            {/* Filterleiste */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                        type="text"
                        value={suche}
                        onChange={(e) => setSuche(e.target.value)}
                        placeholder="Suchen nach Firma, Ort, Website oder Ansprechpartner…"
                        className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                    />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {select('f-status', 'Status', status, setStatus,
                        LEAD_STATUSES.map((s) => ({ value: s, label: LEAD_STATUS_LABELS[s] })))}
                    {select('f-branche', 'Branche', branche, setBranche, branchen)}
                    {select('f-bundesland', 'Bundesland', bundesland, setBundesland,
                        bundeslaender.map((b) => ({ value: b, label: b })))}
                    {select('f-quelle', 'Quelle', quelle, setQuelle,
                        quellen.map((q) => ({ value: q, label: q })))}
                    {select('f-owner', 'Zuständig', owner, setOwner,
                        owners.map((o) => ({ value: o, label: ownerNames?.get(o) || o.slice(0, 8) })))}
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{gefiltert.length} von {leads.length} Leads</span>
                    {!zurueckgesetzt && (
                        <button
                            type="button"
                            onClick={() => {
                                setSuche(''); setStatus(ALLE); setBranche(ALLE);
                                setBundesland(ALLE); setQuelle(ALLE); setOwner(ALLE);
                            }}
                            className="font-bold text-blue-600 hover:text-blue-700"
                        >
                            Filter zurücksetzen
                        </button>
                    )}
                </div>
            </div>

            {/* Tabelle */}
            {gefiltert.length === 0 ? (
                <div className="py-16 text-center text-slate-400 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                    <p className="font-bold">Keine Leads gefunden</p>
                    <p className="text-xs">
                        {zurueckgesetzt ? 'Noch keine Leads angelegt.' : 'Andere Filter versuchen.'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[820px]">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                                    <th className="px-5 py-4 w-[26%]">Firma</th>
                                    <th className="px-5 py-4 w-[16%]">Branche</th>
                                    <th className="px-5 py-4 w-[16%]">Ort</th>
                                    <th className="px-5 py-4 w-[14%]">Quelle</th>
                                    <th className="px-5 py-4 w-[12%]">Status</th>
                                    <th className="px-5 py-4 text-right w-[10%]" title="Score plus Nähe-Zuschlag">
                                        Priorität
                                    </th>
                                    <th className="px-3 py-4 w-[40px]" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {gefiltert.map(({ lead, prio }) => (
                                    <tr
                                        key={lead.id}
                                        onClick={() => onSelect(lead)}
                                        className="hover:bg-blue-50/40 transition-colors group cursor-pointer"
                                    >
                                        <td className="px-5 py-3.5 align-middle">
                                            <span className="font-bold text-slate-800">{lead.name || 'Unbenannt'}</span>
                                            {lead.ansprechpartner && (
                                                <span className="block text-[11px] text-slate-400">{lead.ansprechpartner}</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5 align-middle text-sm text-slate-500">
                                            {lead.branche ? brancheLabel(lead.branche) : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-5 py-3.5 align-middle text-sm text-slate-500">
                                            {[lead.zip, lead.city].filter(Boolean).join(' ') || <span className="text-slate-300">—</span>}
                                            {lead.bundesland && <span className="block text-[11px] text-slate-400">{lead.bundesland}</span>}
                                        </td>
                                        <td className="px-5 py-3.5 align-middle text-[11px] text-slate-500 font-mono truncate">
                                            {lead.quelle}
                                        </td>
                                        <td className="px-5 py-3.5 align-middle">
                                            <span className={`text-[11px] font-bold uppercase rounded px-2 py-1 ${STATUS_BADGE[lead.status]}`}>
                                                {LEAD_STATUS_LABELS[lead.status]}
                                            </span>
                                        </td>
                                        <td
                                            className="px-5 py-3.5 align-middle text-right font-bold text-slate-700 font-mono"
                                            title={prio.begruendung}
                                        >
                                            {lead.score == null
                                                ? <span className="text-slate-300 font-sans font-normal">—</span>
                                                : prio.priority}
                                        </td>
                                        <td className="px-3 py-3.5 align-middle text-right">
                                            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
